<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="3" class="pr-0">
        <h4 class="display-1">{{$t('navigation.evaluations')}}</h4>
      </v-col>

      <v-col cols="12" md="9" class="pl-0">
        <v-row no-gutters>
          <v-col cols="12" md="8" class="pa-0 text-right">
            <v-btn outlined large
              color="primary"
              @click="showModal = !showModal"
            >
              {{ $t('input.generic_link_btn') }}
            </v-btn>
          </v-col>
          <v-col cols="12" md="4" class="pa-0 text-right pt-5 pt-md-0">
            <v-btn large
              color="primary"
              class="ml-2"
              to="/evaluations/create"
            >
              <v-icon left>fa-plus-circle</v-icon>
              {{ $t('evaluations.create') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-card class="mt-5 px-4">
      <v-row>
        <v-col>
          <v-layout row wrap class="ml-3">
            <v-col cols="12" sm="3">
              <v-autocomplete
                :label="$t('evaluations.filter_by')"
                :items="filters"
                prepend-inner-icon="mdi-filter-outline"
                @change="filterItems"
              ></v-autocomplete>
            </v-col>
          </v-layout>

          <x-data-table
            :headers="headers"
            :parent-fetch-data="getEvaluations"
            :options="options"
            no-data="evaluations.no_data"
          >
            <template v-slot:structure="prop">
              <td class="text-center text-capitalize">{{ prop.item.displayName ? prop.item.displayName : prop.item.name }}</td>
              <td class="text-center text-capitalize">{{ prop.item.questionnaire.name }}</td>
              <td class="text-center">
                <v-tooltip top color="blue" :disabled="prop.item.status !== 'creating'">
                  <template v-slot:activator="{ on }">
                    <v-chip outlined label
                      v-on="on"
                      :color="getColor(prop.item.status)"
                    >
                      {{ $t(`evaluations.${prop.item.status}`) }}
                    </v-chip>
                  </template>
                  <span>{{ $t('input.may_take_while') }}</span>
                </v-tooltip>
              </td>
              <td class="text-center">{{ prop.item.deliveredAt | date({date: true, hour: false}) }} hasta {{ prop.item.validUntil | date({date: true, hour: false}) }} </td>
              <td class="text-center">
                <v-tooltip bottom color="primary" v-if="prop.item.evaluated.length">
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon @click="updateModel(prop.item._id)">
                      <v-icon small>mdi-update</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('input.update_model') }}</span>
                </v-tooltip>
                <template v-else-if="prop.item.status !== 'creating' && prop.item.status !== 'editing'">
                  <v-tooltip bottom color="primary" v-if="prop.item.status !== 'completed'">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :to="`evaluations/${prop.item.slug}/edit`"
                        v-on="on"
                        text icon>
                        <v-icon small>edit</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('evaluations.edit') }}</span>
                  </v-tooltip>
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :to="`/evaluations/${prop.item.slug}/details`"
                        v-on="on"
                        text icon>
                        <v-icon small>fa-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('evaluations.details') }}</span>
                  </v-tooltip>
                  <v-tooltip bottom color="primary" v-if="prop.item.status==='completed'">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :to="`/evaluations/reports/${prop.item._id}`"
                        v-on="on"
                        icon
                      >
                        <v-icon small>mdi-chart-bar-stacked</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('input.view_pdf') }}</span>
                  </v-tooltip>
                </template>
                <v-tooltip bottom color="primary" v-else>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      @click="refreshPage"
                    >
                      <v-icon small>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('input.refresh') }}</span>
                </v-tooltip>
              </td>
            </template>
          </x-data-table>
        </v-col>
      </v-row>
    </v-card>
    <v-dialog v-model="showModal" width="650px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('input.copy_invitation_url_title') }}</span>
        </v-card-title>
        <v-card-subtitle>
          <p class="mt-4 mb-0 text-justify body-2">
            {{ $t('input.copy_invitation_url') }}
          </p>
        </v-card-subtitle>
        <v-card-text>
          <v-row justify="center">
            <v-col cols="11">
              <v-text-field
                :label="$t('input.invitation_url')"
                :readonly="true"
                outlined
                v-model="tokenUrl"
                id="tokenUrl"
              ></v-text-field>
            </v-col>
            <v-col cols="1">
              <v-tooltip bottom color="black">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="copyUrl" class="mt-2">
                    <v-icon>file_copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('evaluations.link') }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn block large @click="closeModal">{{ $t('input.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from 'vue'

import { mapState } from 'vuex'
import evaluationsService from '../../services/evaluations'

export default Vue.extend({
  data () {
    return {
      headers: [
        'evaluations.name',
        'evaluations.questionnaire',
        'evaluations.status',
        'evaluations.vigency',
        'evaluations.actions'
      ],
      options: {
        filter: null,
        search: null
      },
      showModal: false,
      tokenUrl: ''
    }
  },
  computed: {
    ...mapState({
      user: state => state.session.user
    }),
    filters () {
      return [
        { text: this.$t('evaluations.all'), value: null },
        { text: this.$t('evaluations.pending'), value: 'pending' },
        { text: this.$t('evaluations.in_progress'), value: 'in_progress' },
        { text: this.$t('evaluations.completed'), value: 'completed' }
      ]
    }
  },
  methods: {
    getColor (status) {
      switch (status) {
        case 'pending':
          return 'gray'
        case 'completed':
          return 'green'
        case 'in_progress':
          return 'blue'
      }
    },
    getEvaluations (options) {
      return evaluationsService.list(options)
    },
    copyUrl () {
      let input = document.getElementById('tokenUrl')
      input.select()
      document.execCommand('copy')
      this.$store.dispatch('alert/success', this.$t('evaluations.link_copied'))
    },
    closeModal () {
      this.showModal = false
    },
    filterItems (value) {
      this.options = {
        ...this.options,
        filter: value
      }
    },
    refreshPage (value) {
      document.location.reload()
    },
    updateModel (id) {
      this.$store.dispatch('loading/show')
      return evaluationsService.updateModel(id)
        .then(() => this.refreshPage())
    }
  },
  created () {
    this.$store.dispatch('session/getSuiteWebHost')
      .then((res) => {
        if (res) {
          const enterpriseToken = this.user.enterprise && this.user.enterprise.invitationCode ? this.user.enterprise.invitationCode : ''
          this.tokenUrl = `${res}/auth/verify-polls/${enterpriseToken}`
        } else {
          next('/404')
        }
      })
  }
})
</script>
